import type { ApolloError } from '@apollo/client';
import { useQuery } from '@apollo/client';
import { useLayoutEffect } from 'react';
import type { ReactNode } from 'react';
import authTokenUtil from '../../account/authTokenUtil';
import { SessionActions } from '../../account/sessionSlice';
import { gql } from '../../graphql/generated/gql';
import { CONTEXT_AUTH_TOKEN_KEY } from '../apollo/getTransportLink';
import { useAppDispatch } from '../redux/useAppSelector';

const CURRENT_SESSION_QUERY = gql(/** @complexity 14 */ `
  query currentSession {
    currentSession {
      ...startSession_session
    }
  }
`);

const isAuthError = (error: ApolloError) =>
  error.networkError &&
  'statusCode' in error.networkError &&
  error.networkError.statusCode === 401;

interface TryLogInProps {
  fallback: ReactNode;
}

const TryLogIn = ({ fallback }: TryLogInProps) => {
  const authToken = authTokenUtil.get();

  const { data, loading, error } = useQuery(CURRENT_SESSION_QUERY, {
    fetchPolicy: 'no-cache',
    skip: !authToken,
    context: { [CONTEXT_AUTH_TOKEN_KEY]: authToken },
  });
  const currentSession = data?.currentSession;

  const dispatch = useAppDispatch();

  useLayoutEffect(() => {
    if (currentSession) {
      dispatch(SessionActions.startSession(currentSession));

      if (window.Sentry?.isEnabled) {
        window.Sentry?.getReplay()?.flush();
      }
    }
  }, [currentSession, dispatch]);

  return loading || currentSession || (error && !isAuthError(error))
    ? null
    : fallback;
};

export default TryLogIn;
